/* eslint-disable array-bracket-newline */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useConnect, useNetwork, useSwitchNetwork } from 'wagmi';
import { ExtensionProvider } from '@multiversx/sdk-extension-provider/out';
import {
  WalletsName,
  WalletsType,
} from '../components/ConnectedWallet/wallets.ls';
import { useWallet } from '../wallet/walletContext';
import { getNetworkFlags } from '../wallet/helpers';
import { actionCreateNotification } from '../actions/notifications';
import { TronWeb } from '../types/tronWeb';

const MULTIVERSX_ERROR = `Ensure you use a ${WalletsName.MultiversX} for trading on the MultiversX Blockchain.`;
const TRON_LINK_ERROR = `Ensure you use a ${WalletsName.TronLink} for trading on the Tron Blockchain.`;
const EVM_ERROR =
  'Ensure you use a EVM Wallet for trading on the EVM Blockchain.';

const useChangeNetwork = () => {
  const { connectors } = useConnect();
  const { chain } = useNetwork();
  const { isLoading, isError, error, switchNetwork } = useSwitchNetwork();
  const { onConnect, onDisconnect } = useWallet();
  const switchRef = useRef<(chainId_?: number | undefined) => void>();

  const dispatch = useDispatch();

  useEffect(() => {
    if (error) {
      const errMessage = (error.cause as any)?.message || error.message;
      dispatch(
        actionCreateNotification({
          kind: 'warning',
          message: errMessage,
        })
      );
    }
  }, [dispatch, error]);

  useEffect(() => {
    switchRef.current = switchNetwork;
  }, [switchNetwork]);

  const changeNetwork = (id: number) => {
    const { isEVM, isElrond, isTron, isTon } = getNetworkFlags(id);

    if (chain) {
      const { isEVM: isEvmPrevious } = getNetworkFlags(chain.id);
      if (isEvmPrevious != isEVM) {
        onDisconnect();
      }

      if (isEVM) {
        if (isEvmPrevious) {
          // we just switched EVM chain
          switchNetwork?.(id);
        } else {
          const injected = connectors.find((c) => c.id == WalletsType.Other);
          if (injected?.ready) {
            onConnect(injected).then(() => {
              if (chain?.id != id) {
                // useRef is used because the regular switchNetwork remains undefined
                // due to the function scope, causing it not to update.
                switchRef.current?.(id);
              }
            });
          } else {
            dispatch(
              actionCreateNotification({
                kind: 'error',
                message: EVM_ERROR,
              })
            );
          }
        }

        return;
      }
    }

    if (isElrond) {
      const multiversX = connectors.find((c) => c.id == WalletsType.MultiversX);
      multiversX
        ?.getProvider()
        .then((provider?: ExtensionProvider) => {
          if (provider?.isInitialized()) {
            onConnect(multiversX);
          } else {
            throw Error();
          }
        })
        .catch(() =>
          dispatch(
            actionCreateNotification({
              kind: 'error',
              message: MULTIVERSX_ERROR,
            })
          )
        );
    }
    if (isTron) {
      const tronLink = connectors.find((c) => c.id == WalletsType.TronLink);
      tronLink
        ?.getProvider()
        .then((provider: TronWeb) => {
          if (provider.ready) {
            onConnect(tronLink);
          } else {
            throw Error();
          }
        })
        .catch(() =>
          dispatch(
            actionCreateNotification({
              kind: 'error',
              message: TRON_LINK_ERROR,
            })
          )
        );
    }
    if (isTon) {
      const ton = connectors.find((c) => c.id == WalletsType.TON);
      onConnect(ton);
    }
  };

  return {
    networkId: chain?.id,
    loading: isLoading,
    error: isError,
    changeNetwork,
  };
};

export default useChangeNetwork;
